import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useTeamEventChangelog } from 'app/frontend/base/config/queries'

type Props = {
  eventId?: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formatChangeValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'nothing'
  if (typeof value === 'string' || typeof value === 'number') return `${value}`
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'object') {
    const asRecord = value as { start_time?: string; finish_time?: string }
    if (asRecord.start_time || asRecord.finish_time) {
      return `${asRecord.start_time ?? '-'} - ${asRecord.finish_time ?? '-'}`
    }
    return JSON.stringify(value)
  }
  return String(value)
}

const EventChangelogDialog = ({ eventId, open, onOpenChange }: Props) => {
  const { t, i18n } = useTranslation()
  const { accessLevel } = useAuth()
  const { team } = useTeam()
  const isAdmin = accessLevel === 'admin'
  const [search, setSearch] = useState('')
  const { data, isFetching } = useTeamEventChangelog(
    open && isAdmin,
    team.id,
    eventId,
  )
  const changelog = data?.changelog ?? []
  const rows = useMemo(() => {
    return changelog.map((entry, index) => {
      const from = formatChangeValue(entry.change.from)
      const to = formatChangeValue(entry.change.to)
      const subject = formatChangeValue(entry.change.subject)
      const key = `dashboard.team.events.changelog.change-types.${entry.type}`
      const description = i18n.exists(key)
        ? t(key, { from, to, subject })
        : t('dashboard.team.events.changelog.unknown-change', {
            type: entry.type,
            from,
            to,
            subject,
          })
      return {
        entry,
        description,
        user: entry.author || '-',
        key: `${entry.changedAt}-${entry.type}-${index}`,
      }
    })
  }, [changelog, i18n, t])
  const searchValue = useMemo(() => search.trim().toLowerCase(), [search])
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (!searchValue) return true
      return (
        row.description.toLowerCase().includes(searchValue) ||
        row.user.toLowerCase().includes(searchValue)
      )
    })
  }, [rows, searchValue])

  if (!isAdmin) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>
            {t('dashboard.team.events.changelog.title')}
          </DialogTitle>
        </DialogHeader>
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
          }}
          placeholder={t('dashboard.team.events.changelog.search-placeholder')}
          className='max-w-sm'
        />
        <div className='max-h-[65vh] overflow-y-auto'>
          {isFetching ? (
            <div className='h-32 flex items-center justify-center text-muted-foreground'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className='h-20 flex items-center justify-center text-sm text-muted-foreground'>
              {searchValue
                ? t('dashboard.team.events.changelog.no-results')
                : t('dashboard.team.events.changelog.empty')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t('dashboard.team.events.changelog.changed-at')}
                  </TableHead>
                  <TableHead>
                    {t('dashboard.team.events.changelog.description')}
                  </TableHead>
                  <TableHead>
                    {t('dashboard.team.events.changelog.user')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map((row) => {
                  const { entry, description, user } = row
                  return (
                    <TableRow key={row.key}>
                      <TableCell className='whitespace-nowrap'>
                        {dayjs(entry.changedAt).format('YYYY-MM-DD HH:mm')}
                      </TableCell>
                      <TableCell>{description}</TableCell>
                      <TableCell>{user}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EventChangelogDialog
